import { IdentitySource } from '@infiltre/shared';
import { useEffect, useRef } from 'react';
import { useSessionStore, useUiStore } from '@/shared/hooks/use-shallow-store';
import { useT } from '@/shared/lib/i18n';
import { localKvStorage } from '@/shared/lib/storage';
import { Button, TopBar } from '@/shared/ui';
import { messageKeyForLobbyError } from '../model/lobby-error';
import { randomGuestName } from '../model/random-name';
import { loadSavedName } from '../model/saved-name';
import { useLobbyActions } from '../model/use-lobby-actions';

export type LobbyCreateFormProps = {
  /** Called once a room has been created server-side (REST roundtrip succeeded). */
  onCreated: (code: string) => void;
};

/**
 * Direct-hit fallback for `/online/create`. There is no name prompt — the
 * page auto-creates on mount using the platform identity name, the
 * last-saved name, or a random guest fallback. The home page's primary
 * "Online" CTA bypasses this route entirely; this component only matters
 * for bookmarks / refreshes that land on the URL directly. On failure we
 * surface a retry button so the user is not stranded.
 */
export const LobbyCreateForm = ({ onCreated }: LobbyCreateFormProps) => {
  const t = useT();
  const locale = useUiStore((s) => s.locale);
  const { createRoom } = useLobbyActions({ onCreated });
  const lobbyError = useSessionStore((s) => s.lobbyError);
  const lobbyBusy = useSessionStore((s) => s.lobbyBusy);
  const identity = useSessionStore((s) => s.identity);

  const triggered = useRef(false);
  const attempt = (): void => {
    const fromIdentity = identity && identity.source !== IdentitySource.Anon ? identity.name : null;
    const explicit = fromIdentity ?? loadSavedName(localKvStorage);
    const name = explicit ?? randomGuestName(locale);
    void createRoom(name, { persistName: !!explicit });
  };

  useEffect(() => {
    if (triggered.current) return;
    triggered.current = true;
    attempt();
    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only auto-create; re-running on hook reference flips would double-create.
  }, [attempt]);

  const errorKey = lobbyError ? messageKeyForLobbyError(lobbyError) : null;
  const errorText = errorKey ? t(errorKey) : lobbyError;

  return (
    <main className="mx-auto flex h-full w-full max-w-md flex-col gap-6 px-5 py-4">
      <TopBar />
      <header className="pt-1">
        <div className="eyebrow text-und tracking-[0.2em]">{t('online.create.eyebrow')}</div>
        <h1 className="mt-1 font-display font-extrabold text-[40px] text-ink leading-[0.95] tracking-[-0.02em] sm:text-[52px]">
          {t('online.create.heading')}
        </h1>
        <p className="mt-2.5 text-[14px] text-text-dim italic leading-[1.45]">
          {lobbyError ? errorText : t('online.create.creating')}
        </p>
      </header>

      <div className="mt-auto pt-2">
        {lobbyError ? (
          <Button variant="orange" onClick={attempt} disabled={lobbyBusy}>
            {lobbyBusy ? t('online.create.creating') : t('online.create.submit')}
          </Button>
        ) : (
          <p className="text-center font-display text-[13px] text-text-dim uppercase tracking-[0.16em]">
            {t('online.create.creating')}
          </p>
        )}
      </div>
    </main>
  );
};
